"use client";

import { c as _c } from "react/compiler-runtime";
import { ArrowRight, Check, Clock, Copy } from "lucide-react";
import Link from "next/link";
import { useState } from "react";
import { CodeCTATracker } from "@/components/LandingTracker";
import { Button } from "@/components/ui/button";
import { AnthropicIcon, EmpryoIcon, OpenCodeIcon, SoulForgeIcon } from "@llmgateway/shared/components";
import type { ComponentType, SVGProps } from "react";
const API_BASE = "https://api.llmgateway.io";
interface Step {
  label: string;
  code?: string;
}
interface ToolGuide {
  id: string;
  label: string;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  blurb: string;
  steps: Step[];
}
const OpenAiGlyph: ComponentType<SVGProps<SVGSVGElement>> = props => {
  const $ = _c(4);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <rect x="3" y="3" width="18" height="18" rx="4" strokeWidth="1.5" />;
    t1 = <path d="M8 12h8M12 8v8" strokeWidth="1.5" strokeLinecap="round" />;
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  let t2;
  if ($[2] !== props) {
    t2 = <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" {...props}>{t0}{t1}</svg>;
    $[2] = props;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
};
const TOOLS: ToolGuide[] = [{
  id: "claude-code",
  label: "Claude Code",
  icon: AnthropicIcon,
  blurb: "Two env vars. No SDK changes, no reinstall.",
  steps: [{
    label: "Point Claude Code at DevPass",
    code: `export ANTHROPIC_BASE_URL=${API_BASE}\nexport ANTHROPIC_AUTH_TOKEN=<your-devpass-key>`
  }, {
    label: "Run it — switch models any time with /model",
    code: "claude"
  }]
}, {
  id: "opencode",
  label: "OpenCode",
  icon: OpenCodeIcon,
  blurb: "Built in. No env vars, no config files.",
  steps: [{
    label: "Launch OpenCode",
    code: "opencode"
  }, {
    label: "Type /connect, pick LLM Gateway, paste your DevPass key"
  }]
}, {
  id: "empryo",
  label: "Empryo",
  icon: EmpryoIcon,
  blurb: "Edits symbols, not strings. One key, every model.",
  steps: [{
    label: "Launch Empryo",
    code: "empryo"
  }, {
    label: "Type /keys and paste your DevPass key"
  }]
}, {
  id: "soulforge",
  label: "SoulForge",
  icon: SoulForgeIcon,
  blurb: "Paste one key and the whole catalog is live.",
  steps: [{
    label: "Launch SoulForge",
    code: "soulforge"
  }, {
    label: "Type /keys and paste your DevPass key"
  }]
}, {
  id: "openai",
  label: "Anything else",
  icon: OpenAiGlyph,
  blurb: "Cursor, Cline, Aider, Continue — any OpenAI-compatible tool.",
  steps: [{
    label: "Set the base URL and key in your tool's settings",
    code: `Base URL: ${API_BASE}/v1\nAPI key:  <your-devpass-key>`
  }, {
    label: "Pick any model id — Claude, GPT, Gemini, GLM, Qwen…"
  }]
}];
function CopyBlock(t0) {
  const $ = _c(12);
  const {
    code
  } = t0;
  const [copied, setCopied] = useState(false);
  let t1;
  if ($[0] !== code) {
    t1 = async () => {
      if (!navigator.clipboard?.writeText) {
        return;
      }
      try {
        await navigator.clipboard.writeText(code);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      } catch {
        setCopied(false);
      }
    };
    $[0] = code;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const handleCopy = t1;
  let t2;
  if ($[2] !== code) {
    t2 = <pre className="overflow-x-auto px-3.5 py-3 font-mono text-xs leading-6 text-foreground/90">{code}</pre>;
    $[2] = code;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== copied) {
    t3 = copied ? <Check className="h-3.5 w-3.5 text-emerald-500" /> : <Copy className="h-3.5 w-3.5" />;
    $[4] = copied;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== handleCopy || $[7] !== t3) {
    t4 = <button type="button" onClick={handleCopy} aria-label="Copy command" className="absolute right-2 top-2 rounded-md border border-border/60 bg-card p-1.5 text-muted-foreground transition-colors hover:text-foreground">{t3}</button>;
    $[6] = handleCopy;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t2 || $[10] !== t4) {
    t5 = <div className="group relative overflow-hidden rounded-lg border border-border/60 bg-background">{t2}{t4}</div>;
    $[9] = t2;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return t5;
}
export function SwitchIn60() {
  const $ = _c(37);
  const [active, setActive] = useState(TOOLS[0].id);
  let t0;
  let t1;
  let t10;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[0] !== active) {
    const tool = TOOLS.find(t => t.id === active) ?? TOOLS[0];
    t9 = "switch-in-60";
    t10 = "border-t bg-muted/20 px-4 py-16";
    t4 = "container mx-auto max-w-3xl";
    if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <div className="mb-1.5 inline-flex items-center gap-1.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-3 py-1 text-xs font-medium text-emerald-600 dark:text-emerald-400"><Clock className="h-3.5 w-3.5" />Switch in 60 seconds</div>;
      t6 = <h2 className="mt-3 text-2xl font-bold tracking-tight sm:text-3xl">Keep your editor. Point it at DevPass.</h2>;
      t7 = <p className="mt-2 max-w-2xl text-muted-foreground">No migration project, no rewrite. Your existing tool, every model, one key. Switching back is just as fast — so there's nothing to lose.</p>;
      $[12] = t5;
      $[13] = t6;
      $[14] = t7;
    } else {
      t5 = $[12];
      t6 = $[13];
      t7 = $[14];
    }
    const t11 = TOOLS.map(t_0 => {
      const Icon = t_0.icon;
      const isActive = t_0.id === active;
      return <button key={t_0.id} type="button" onClick={() => setActive(t_0.id)} className={`inline-flex items-center gap-2 rounded-lg border px-3.5 py-2 text-sm font-medium transition-colors ${isActive ? "border-foreground/30 bg-card text-foreground shadow-sm" : "border-border/60 text-muted-foreground hover:text-foreground"}`}><Icon className="h-4 w-4" />{t_0.label}</button>;
    });
    if ($[15] !== t11) {
      t8 = <div className="mt-7 flex flex-wrap gap-2">{t11}</div>;
      $[15] = t11;
      $[16] = t8;
    } else {
      t8 = $[16];
    }
    t2 = "mt-5 rounded-2xl border bg-card p-5 sm:p-6";
    t3 = <p className="mb-5 text-sm text-muted-foreground">{tool.blurb}</p>;
    t0 = "space-y-4";
    t1 = tool.steps.map(_temp);
    $[0] = active;
    $[1] = t0;
    $[2] = t1;
    $[3] = t10;
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
    $[7] = t5;
    $[8] = t6;
    $[9] = t7;
    $[10] = t8;
    $[11] = t9;
  } else {
    t0 = $[1];
    t1 = $[2];
    t10 = $[3];
    t2 = $[4];
    t3 = $[5];
    t4 = $[6];
    t5 = $[7];
    t6 = $[8];
    t7 = $[9];
    t8 = $[10];
    t9 = $[11];
  }
  let t11;
  if ($[17] !== t0 || $[18] !== t1) {
    t11 = <ol className={t0}>{t1}</ol>;
    $[17] = t0;
    $[18] = t1;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <CodeCTATracker cta="get_started" location="compare_switch_in_60"><Button className="gap-2" asChild={true}><Link href="/signup?plan=pro">Get your DevPass key<ArrowRight className="h-4 w-4" /></Link></Button></CodeCTATracker>;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  let t13;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <div className="mt-6 flex flex-col gap-3 border-t pt-5 sm:flex-row sm:items-center">{t12}<p className="text-xs text-muted-foreground">Need the full walkthrough? See the{" "}<Link href="/guides" className="underline hover:text-foreground">setup guides</Link>.</p></div>;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  let t14;
  if ($[22] !== t11 || $[23] !== t2 || $[24] !== t3) {
    t14 = <div className={t2}>{t3}{t11}{t13}</div>;
    $[22] = t11;
    $[23] = t2;
    $[24] = t3;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  let t15;
  if ($[26] !== t14 || $[27] !== t4 || $[28] !== t5 || $[29] !== t6 || $[30] !== t7 || $[31] !== t8) {
    t15 = <div className={t4}>{t5}{t6}{t7}{t8}{t14}</div>;
    $[26] = t14;
    $[27] = t4;
    $[28] = t5;
    $[29] = t6;
    $[30] = t7;
    $[31] = t8;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  let t16;
  if ($[33] !== t10 || $[34] !== t15 || $[35] !== t9) {
    t16 = <section id={t9} className={t10}>{t15}</section>;
    $[33] = t10;
    $[34] = t15;
    $[35] = t9;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  return t16;
}
function _temp(step, index) {
  return <li key={step.label} className="flex gap-3.5"><span className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full border border-border/60 bg-background text-xs font-semibold tabular-nums text-muted-foreground">{index + 1}</span><div className="min-w-0 flex-1 space-y-2"><p className="text-sm font-medium text-foreground">{step.label}</p>{step.code && <CopyBlock code={step.code} />}</div></li>;
}